import React, { useState, useEffect, useRef } from 'react';
import { GripVertical, ChevronRight } from 'lucide-react';
import TypewriterText from './TypewriterText';
import './CustomTypesSection.css';

const WINDOW_TYPES = [
  { name: 'Standard Window', desc: 'Exterior/Interior', price: '10.00' },
  { name: 'French Panes', desc: 'Exterior/Interior', price: '3.50' },
  { name: 'Screens', desc: 'Exterior/Interior', price: '8.00' },
  { name: 'Skylights', desc: 'Exterior/Interior', price: '12.00' },
];

const CUSTOMER_TYPES = [
  {
    id: 'residential',
    name: 'Residential',
    windowTypes: [
      { name: 'Standard Window', price: '10.00' },
      { name: 'French Panes', price: '3.50' },
      { name: 'Screens', price: '8.00' },
      { name: 'Skylights', price: '12.00' },
    ],
  },
  {
    id: 'storefront',
    name: 'Storefront',
    windowTypes: [
      { name: 'Standard', price: '10.00' },
      { name: 'XL Pane', price: '15.00' },
      { name: 'Doors', price: '25.00' },
    ],
  },
  {
    id: 'construction',
    name: 'Construction',
    windowTypes: [
      { name: 'Standard', price: '10.00' },
      { name: 'XL Pane', price: '15.00' },
      { name: 'French Pane', price: '3.50' },
      { name: 'Skylight', price: '12.00' },
    ],
  },
];

function TypewriterWindowTile({ name, desc, price, isActive, showCursor, onComplete }) {
  const [phase, setPhase] = useState('idle');

  useEffect(() => {
    if (isActive && phase === 'idle') {
      setPhase('name');
    }
  }, [isActive, phase]);

  const handleNameComplete = () => {
    setTimeout(() => setPhase('price'), 300);
  };

  const handlePriceComplete = () => {
    setPhase('done');
    onComplete?.();
  };

  const isEditing = phase === 'name' || phase === 'price';

  return (
    <div className={`custom-types__window-tile ${isEditing ? 'custom-types__window-tile--editing' : ''}`}>
      <GripVertical className="custom-types__grip" aria-hidden />
      <div className="custom-types__window-tile-content">
        <div className="custom-types__window-tile-left">
          <div className="custom-types__window-tile-name">
            {phase === 'name' ? (
              <TypewriterText
                text={name}
                tag="span"
                onComplete={handleNameComplete}
                reserveSpace
              />
            ) : phase === 'idle' ? (
              <span className="custom-types__window-tile-placeholder" aria-hidden>{name}</span>
            ) : (
              name
            )}
          </div>
          <div className="custom-types__window-tile-desc">{desc}</div>
        </div>
        <div className="custom-types__window-tile-price">
          {phase === 'price' ? (
            <TypewriterText
              text={`$${price}`}
              tag="span"
              onComplete={handlePriceComplete}
              reserveSpace
            />
          ) : phase === 'done' ? (
            <>
              ${price}
              {showCursor && <span className="custom-types__cursor" aria-hidden />}
            </>
          ) : (
            <span className="custom-types__window-tile-price-placeholder" aria-hidden>&#8203;</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CustomTypesSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTileIndex, setActiveTileIndex] = useState(-1);
  const triggerRef = useRef(null);
  const hasTriggered = useRef(false);
  const activeCustomer = CUSTOMER_TYPES[activeIndex];

  useEffect(() => {
    const el = triggerRef.current;
    if (!el || hasTriggered.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (hasTriggered.current) return;
        const [entry] = entries;
        if (entry.isIntersecting) {
          hasTriggered.current = true;
          setActiveTileIndex(0);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="landing__custom">
      <h2 className="landing__custom-headline">
        Create custom Window types, apply pricing to your Unique Customers
      </h2>

      <div className="landing__custom-grid">
        <div className="landing__custom-col landing__custom-col--windows">
          <h3 className="landing__custom-col-title">Window Types</h3>
          <div className="custom-types__window-list">
            {WINDOW_TYPES.map((wt, i) => (
              <div key={wt.name} ref={i === 1 ? triggerRef : undefined}>
                <TypewriterWindowTile
                  name={wt.name}
                  desc={wt.desc}
                  price={wt.price}
                  isActive={activeTileIndex === i}
                  showCursor={i === 3}
                  onComplete={() => setActiveTileIndex((prev) => Math.min(prev + 1, 4))}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="landing__custom-divider" aria-hidden />

        <div className="landing__custom-col landing__custom-col--customers">
          <h3 className="landing__custom-col-title">Customer Types</h3>
          <div className="custom-types__customer-list">
            {CUSTOMER_TYPES.map((ct, i) => (
              <button
                key={ct.id}
                type="button"
                className={`custom-types__customer-item ${i === activeIndex ? 'custom-types__customer-item--active' : ''}`}
                onClick={() => setActiveIndex(i)}
              >
                <GripVertical className="custom-types__grip" aria-hidden />
                <div className="custom-types__customer-item-content">
                  <span className="custom-types__customer-item-name">{ct.name}</span>
                  <span className="custom-types__customer-item-count">
                    {ct.windowTypes.length} window types configured
                  </span>
                </div>
                <ChevronRight className="custom-types__chevron" aria-hidden />
              </button>
            ))}
          </div>

          <div className="custom-types__carousel">
            <div className="custom-types__carousel-inner">
              <div className="custom-types__carousel-card">
                <div className="custom-types__carousel-header">{activeCustomer.name}</div>
                <div className="custom-types__carousel-windows">
                  {activeCustomer.windowTypes.map((wt) => (
                    <div key={wt.name} className="custom-types__carousel-window-row">
                      <span className="custom-types__carousel-window-name">{wt.name}</span>
                      <span className="custom-types__carousel-window-price">${wt.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
